import type { ComponentChildren } from "preact";

export const isLocal = () => document.location.hostname === "localhost";

export function isDevice(): boolean {
    return (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent.toLowerCase()));
}

export function isMobile(): boolean {
    return isDevice() && window.devicePixelRatio >= 2;
}

export function isTablet(): boolean {
    return isMobile() && window.devicePixelRatio < 2;
}

export const isPWA = () => !!window.matchMedia('(display-mode: standalone)').matches;

// --- copied from https://www.npmjs.com/package/fast-deep-equal
const isArray = Array.isArray;
const keyList = Object.keys;
const hasProp = Object.prototype.hasOwnProperty;

//@ts-ignore
export function equal(a: any, b: any) {
    if (a === b) return true;

    if (a && b && typeof a == "object" && typeof b == "object") {
        var arrA = isArray(a),
            arrB = isArray(b),
            i,
            length,
            key;

        if (arrA && arrB) {
            length = a.length;
            if (length != b.length) return false;
            for (i = length; i-- !== 0;) if (!equal(a[i], b[i])) return false;
            return true;
        }

        if (arrA != arrB) return false;

        var dateA = a instanceof Date,
            dateB = b instanceof Date;
        if (dateA != dateB) return false;
        if (dateA && dateB) return a.getTime() == b.getTime();

        var regexpA = a instanceof RegExp,
            regexpB = b instanceof RegExp;
        if (regexpA != regexpB) return false;
        if (regexpA && regexpB) return a.toString() == b.toString();

        var keys = keyList(a);
        length = keys.length;

        if (length !== keyList(b).length) return false;

        for (i = length; i-- !== 0;) if (!hasProp.call(b, keys[i])) return false;

        for (i = length; i-- !== 0;) {
            key = keys[i];
            if (!equal(a[key], b[key])) return false;
        }

        return true;
    }

    return a !== a && b !== b;
}

export function setColorScheme(mode: "auto" | "light" | "dark") {
    if (mode === "light" || mode == "dark") {
        document.documentElement.setAttribute("data-color-scheme", mode);
    } else {
        document.documentElement.removeAttribute("data-color-scheme");
    }
    // localStorage.setItem("color-scheme", mode);
}

export function getColorScheme() {
    return document.documentElement.getAttribute("data-color-scheme") ?? "auto";
}

export function isComponentChildrenEmpty(
    children: ComponentChildren | undefined
) {
    if (!children) return true;
    if (Array.isArray(children)) return children.length === 0;
    if (typeof children === "string") return children.trim() === "";
    return false;
}

export class ConcurrentEntryGuard<T> {
    promise: Promise<T> | null = null;
    call = async (callback: () => Promise<T>) => {
        if (this.promise) return await this.promise;
        this.promise = callback();
        try {
            return await this.promise;
        } finally {
            this.promise = null;
        }
    };
}

export function base64Decode(text: string): string | undefined {
    try {
        return atob(text);
    } catch (e) { }
    try {
        let cleaned = text
            .trim() // Remove leading/trailing whitespace
            .replace(/\s/g, "") // Remove all whitespace
            .replace(/-/g, "+") // URL-safe to standard
            .replace(/_/g, "/"); // URL-safe to standard

        // Add padding if needed
        const padding = cleaned.length % 4;
        if (padding > 0) {
            cleaned += "=".repeat(4 - padding);
        }

        return atob(cleaned);
    } catch (e) { }
    return undefined;
}

export function getAbsoluteYPosition(element: HTMLElement, subtractScroll = false): number {
    let y = 0;
    let currentElement: HTMLElement | null = element;

    while (currentElement) {
        y += currentElement.offsetTop - (!subtractScroll ? 0 : (currentElement.offsetParent ? (currentElement.offsetParent as HTMLElement).scrollTop : 0));
        currentElement = currentElement.offsetParent as HTMLElement | null;
    }

    return y;
}

export function formatDateTime(date: Date | string | undefined, separator: string = " "): string {
    if (!date) {
        return "";
    }
    if (typeof date == "string") {
        if (date.endsWith("Z")) date = date.substring(0, date.length - 1);
        // if (!date.endsWith("Z")) date += "Z";
        const tmp = new Date(date);
        if (!isNaN(tmp.valueOf())) {
            date = tmp;
        } else {
            date = date.replace("T", " ");
            let index = date.indexOf("+");
            if (index != -1) {
                date = date.replace("+", " (+") + ")";
            }
            index = date.indexOf(".");
            if (index != -1) {
                date = date.substring(0, index);
            }

            // remove seconds
            const hasSeconds = date.split(':').length == 3;
            if (hasSeconds) {
                index = date.lastIndexOf(':');
                date = date.substring(0, index);
            }

            return date;
        }
    }
    if (date instanceof Date && !isNaN(date.valueOf())) {
        return (
            date.getFullYear() + "-" +
            ("0" + (date.getMonth() + 1)).slice(-2) + "-" +
            ("0" + date.getDate()).slice(-2) +
            separator +
            ("0" + date.getHours()).slice(-2) + ":" +
            ("0" + date.getMinutes()).slice(-2)
        );
    }
    return "";
}

export function isValidEmail(email: string | null | undefined) {
    return !!email && email.length > 2 && -1 !== email.indexOf("@");
}
